// ===== Learning With Errors (LWE) math for the LWE lab =====
//
// LWE is the clean toy model behind a major part of module-lattice PQC.
// ML-KEM is based on Module-LWE, while ML-DSA combines Module-LWE with
// short-integer-solution style assumptions. You publish many noisy linear
// equations over Z_q:
//
//     b_i = <a_i, s> + e_i   (mod q)
//
// with a secret vector s, random public rows a_i, and small errors e_i.
// Recovering s is easy if the errors are zero (it is just a linear system you
// solve by Gaussian elimination). The small errors are what make it hard:
// elimination multiplies rows by constants mod q, which amplifies a tiny error
// into a uniformly random value and destroys the signal. The honest way to
// recover s is to treat b as a point close to a lattice point, i.e. a Bounded
// Distance Decoding / Closest Vector problem, which is hard in high dimension.
//
// Everything here is exact integer arithmetic mod a small prime so it can be
// unit tested and rendered as colored number grids in the browser. The hardness
// is a high-dimension phenomenon; small n and q just make it legible.

export interface LweInstance {
  n: number; // secret dimension
  m: number; // number of samples (equations)
  q: number; // prime modulus
  A: number[][]; // m x n, entries in [0, q)
  s: number[]; // length n, the secret in [0, q)
  e: number[]; // length m, small errors (stored in [0, q), read via centered())
  b: number[]; // length m, = (A s + e) mod q
}

// ===== modular arithmetic =====

/** Non-negative remainder in [0, q). */
export function mod(x: number, q: number): number {
  return ((x % q) + q) % q;
}

/** Map a residue to its balanced representative in (-q/2, q/2]. This is the
 * "how big is the error really" view: 1 and q-1 are both tiny (+1 and -1). */
export function centered(x: number, q: number): number {
  const r = mod(x, q);
  return r > q / 2 ? r - q : r;
}

/** Modular inverse via the extended Euclidean algorithm. Returns null when a
 * is not invertible mod q (only happens for non-prime q). */
export function modInverse(a: number, q: number): number | null {
  let [old_r, r] = [mod(a, q), q];
  let [old_s, s] = [1, 0];
  while (r !== 0) {
    const quot = Math.floor(old_r / r);
    [old_r, r] = [r, old_r - quot * r];
    [old_s, s] = [s, old_s - quot * s];
  }
  if (old_r !== 1) return null;
  return mod(old_s, q);
}

export function dotMod(a: number[], s: number[], q: number): number {
  let acc = 0;
  for (let i = 0; i < a.length; i++) acc += a[i] * s[i];
  return mod(acc, q);
}

// ===== matrix helpers mod q =====

/** Gauss-Jordan inverse of a square matrix mod a prime q. Null if singular. */
export function matInverseMod(M: number[][], q: number): number[][] | null {
  const n = M.length;
  // Augment [M | I].
  const a = M.map((row, i) => [
    ...row.map((v) => mod(v, q)),
    ...Array.from({ length: n }, (_, j) => (i === j ? 1 : 0)),
  ]);

  for (let col = 0; col < n; col++) {
    // Find a pivot row with a nonzero entry in this column.
    let pivot = -1;
    for (let row = col; row < n; row++) {
      if (a[row][col] % q !== 0) {
        pivot = row;
        break;
      }
    }
    if (pivot === -1) return null;
    [a[col], a[pivot]] = [a[pivot], a[col]];

    const inv = modInverse(a[col][col], q);
    if (inv === null) return null;
    for (let j = 0; j < 2 * n; j++) a[col][j] = mod(a[col][j] * inv, q);

    for (let row = 0; row < n; row++) {
      if (row === col) continue;
      const factor = a[row][col];
      if (factor === 0) continue;
      for (let j = 0; j < 2 * n; j++) {
        a[row][j] = mod(a[row][j] - factor * a[col][j], q);
      }
    }
  }
  return a.map((row) => row.slice(n));
}

export function matVecMod(M: number[][], v: number[], q: number): number[] {
  return M.map((row) => dotMod(row, v, q));
}

// ===== seeded RNG (mulberry32) =====

/** Deterministic PRNG so instances are reproducible and tests are stable. */
export function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ===== instance generation =====

function randInt(rng: () => number, q: number): number {
  return Math.floor(rng() * q) % q;
}

/** Build an LWE instance. The leading n x n block of A is forced invertible so
 * that the noiseless case is exactly solvable (which makes the noisy contrast
 * sharp). Errors are drawn uniformly from [-noiseWidth, noiseWidth]. */
export function generateLwe(
  n: number,
  m: number,
  q: number,
  noiseWidth: number,
  rng: () => number
): LweInstance {
  const s = Array.from({ length: n }, () => randInt(rng, q));

  // Resample the first n rows until they form an invertible matrix mod q.
  let head: number[][] = [];
  for (let attempt = 0; attempt < 1000; attempt++) {
    head = Array.from({ length: n }, () =>
      Array.from({ length: n }, () => randInt(rng, q))
    );
    if (matInverseMod(head, q) !== null) break;
  }

  const A: number[][] = [...head.map((r) => [...r])];
  for (let i = n; i < m; i++) {
    A.push(Array.from({ length: n }, () => randInt(rng, q)));
  }

  const e: number[] = [];
  const b: number[] = [];
  for (let i = 0; i < m; i++) {
    const err = noiseWidth === 0 ? 0 : Math.round((rng() * 2 - 1) * noiseWidth);
    e.push(mod(err, q));
    b.push(mod(dotMod(A[i], s, q) + err, q));
  }

  return { n, m, q, A, s, e, b };
}

// ===== solving by elimination =====

export interface SolveResult {
  recovered: number[] | null; // null if the leading block is singular
  exact: boolean; // recovered equals the true secret
  inverse: number[][] | null; // (A_head)^-1 mod q
  errorSub: number[]; // the errors of the n used equations, centered
  amplified: number[]; // (A_head)^-1 * e_sub mod q, centered (= recovered - s)
}

/** Solve the first n equations as a square linear system mod q. With zero
 * noise this returns the exact secret. With noise the recovered vector is
 * s + (A_head)^-1 * e_sub: a small error smeared across all of Z_q. */
export function solveByElimination(inst: LweInstance): SolveResult {
  const { n, q, A, b, e, s } = inst;
  const head = A.slice(0, n);
  const inverse = matInverseMod(head, q);
  const errorSub = e.slice(0, n).map((x) => centered(x, q));

  if (inverse === null) {
    return { recovered: null, exact: false, inverse: null, errorSub, amplified: [] };
  }

  const bHead = b.slice(0, n);
  const recovered = matVecMod(inverse, bHead, q);
  const amplified = matVecMod(inverse, e.slice(0, n), q).map((x) => centered(x, q));
  const exact = recovered.every((v, i) => v === s[i]);

  return { recovered, exact, inverse, errorSub, amplified };
}

// ===== residuals and brute-force search =====

/** Per-equation centered residual b_i - <a_i, candidate> for a guessed secret.
 * At the true secret these are exactly the (small) errors. */
export function residuals(inst: LweInstance, candidate: number[]): number[] {
  return inst.b.map((bi, i) =>
    centered(bi - dotMod(inst.A[i], candidate, inst.q), inst.q)
  );
}

/** Total absolute residual: the objective a search minimizes. Minimal at the
 * true secret when the noise is small. */
export function residual(inst: LweInstance, candidate: number[]): number {
  return residuals(inst, candidate).reduce((acc, r) => acc + Math.abs(r), 0);
}

export interface SearchResult {
  grid: number[][]; // grid[s0][s1] = min total residual over remaining coords
  best: { s: number[]; residual: number };
  searchSize: number; // q^n, the true search space
}

/** Exhaustively score every candidate secret and project the objective onto
 * the first two coordinates (min over the rest) for a 2D heatmap. Only viable
 * for the tiny demo parameters; returns null past `maxSize`. */
export function searchHeatmap(inst: LweInstance, maxSize = 100000): SearchResult | null {
  const { n, q } = inst;
  const searchSize = q ** n;
  if (searchSize > maxSize) return null;

  const grid: number[][] = Array.from({ length: q }, () =>
    Array.from({ length: q }, () => Infinity)
  );
  let best = { s: [] as number[], residual: Infinity };

  const cand = new Array(n).fill(0);
  const recurse = (idx: number): void => {
    if (idx === n) {
      const r = residual(inst, cand);
      const s0 = cand[0];
      const s1 = n >= 2 ? cand[1] : 0;
      if (r < grid[s0][s1]) grid[s0][s1] = r;
      if (r < best.residual) best = { s: [...cand], residual: r };
      return;
    }
    for (let v = 0; v < q; v++) {
      cand[idx] = v;
      recurse(idx + 1);
    }
  };
  recurse(0);

  return { grid, best, searchSize };
}
